import React from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from 'primereact/button';
import FormInputText from '../../components/form/FormInputText';
import FormPassword from '../../components/form/FormPassword';
import { IntegrationSettings } from '../../models/integrationSettings';
import { IntegrationStatus, IntegrationVendor } from '../../models/enums/integrationEnums';
import { parseIntegrationData } from '../../utils/misc-utils';

type PlaidVoaFormValues = {
  clientId: string;
  secret: string;
};

interface EditPlaidVoaIntegrationSettingsProps {
  onCancel: () => void;
  onSave?: (settings: IntegrationSettings) => void;
  integrationSettings?: IntegrationSettings;
  environment: string;
}

export const EditPlaidVoaIntegrationSettings = ({
  onCancel,
  onSave,
  integrationSettings,
  environment,
}: EditPlaidVoaIntegrationSettingsProps): JSX.Element => {
  const isEditMode = !!integrationSettings;

  let clientId = '';
  let secret = '';
  if (isEditMode && integrationSettings?.integrationData) {
    const data = parseIntegrationData(integrationSettings.integrationData);
    clientId = data.clientId || '';
    secret = data.secret || '';
  }

  const validationSchema = yup.object().shape({
    clientId: yup.string().required('Client ID is required'),
    secret: yup.string().required('Secret is required'),
  });

  const { control, handleSubmit, formState } = useForm<PlaidVoaFormValues>({
    defaultValues: {
      clientId: clientId,
      secret: secret,
    },
    resolver: yupResolver(validationSchema),
    mode: 'onSubmit',
  });

  const { errors } = formState;

  const onSubmit = (data: PlaidVoaFormValues) => {

    if (onSave) {
      let settings: IntegrationSettings;

      if (isEditMode && integrationSettings) {
        settings = {
          ...integrationSettings,
          environment: environment,
          integrationData: JSON.stringify({
            clientId: data.clientId,
            secret: data.secret,
          }),
        };
      } else {
        settings = {
          name: 'Plaid VOA Integration',
          vendor: IntegrationVendor.Plaid,
          status: IntegrationStatus.Active,
          environment: environment,
          integrationData: JSON.stringify({
            clientId: data.clientId,
            secret: data.secret,
          }),
        };
      }

      onSave(settings);
    }
  };

  return (
    <div className="surface-section p-4 border-1 surface-border border-round mb-3">
      <h3 className="mt-0 mb-3">{isEditMode ? 'Edit Plaid VOA Settings' : 'Plaid VOA Settings'}</h3>
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="pb-3 p-fluid p-input-filled">
        <div className="field mb-4">
          <FormInputText
            control={control}
            errors={errors}
            field="clientId"
            label="Client ID *"
            placeholder="Enter your Plaid Client ID"
          />
        </div>

        <div className="field mb-4">
          <FormPassword
            control={control}
            errors={errors}
            field="secret"
            label="Secret *"
            placeholder="Enter your Plaid Secret"
            feedback={false}
            toggleMask
          />
        </div>

        <div className="flex justify-content-end gap-2 pt-4">
          <Button
            type="button"
            label="Cancel"
            className="p-button-outlined"
            style={{ width: 'auto', minWidth: 'auto' }}
            onClick={onCancel}
          />
          <Button
            type="submit"
            label={isEditMode ? 'Update' : 'Save'}
            className="p-button"
            style={{ width: 'auto', minWidth: 'auto' }}
          />
        </div>
      </form>
    </div>
  );
};
